/**
 * The booked page (`/booked/:bookingId`, booked-web.html Direction 2): the
 * check, Booking confirmed, the ticket (the offering and who with, when, length,
 * where with directions, the payment), Add to Calendar and View my booking,
 * and whether the confirmation reached Telegram.
 */
import type { SitePhoto } from '@telify/shared';

import { definePiece } from '../../contract/define';

export const contract = definePiece({
  name: 'booked',
  description:
    "The booked page: Booking confirmed, the booking's ticket, Add to Calendar, View my booking, and the Telegram line.",
  options: {},
  parts: {
    page: { description: 'The whole page.', required: false },
    title: { description: 'Booking confirmed.', required: true, role: 'title' },
    ticket: { description: "The booking's ticket: the offering and its rows.", required: true },
    row: { description: 'One row of the ticket: When, Length or Format, Where, the payment.', required: true },
    amount: { description: "A ticket row's amount, at its end.", required: false },
    directions: { description: 'Get directions, right of a place.', required: false },
    preparation: { description: "The studio's preparation note. The standard look leaves it out: Telegram sends it after the Booked message.", required: false },
    addToCalendar: { description: 'Add to Calendar, right of When: opens its menu.', required: false },
    calendarMenu: { description: "Add to Calendar's menu: Google Calendar, Outlook and Apple Calendar.", required: false },
    calendarGoogle: { description: 'Google Calendar: opens it with the booking filled in.', required: false },
    calendarOutlook: { description: 'Outlook: opens Outlook on the web with the booking filled in.', required: false },
    calendarApple: { description: 'Apple Calendar: downloads the calendar file.', required: false },
    viewBooking: { description: 'View my booking.', required: false, role: 'primary-action' },
    telegramLine: { description: 'Whether the confirmation reached the client\'s Telegram chat, or Open chat when it could not.', required: true },
    openChat: { description: "Open chat: the studio's bot in Telegram, when the confirmation could not be delivered.", required: true, role: 'secondary-action' },
    problem: { description: 'Why the calendar file could not be opened.', required: true },
  },
  actions: {
    addToCalendar: { description: "Downloads the booking as a calendar file (the menu's Apple Calendar).", args: [] },
  },
  events: {
    addedToCalendar: 'The client downloaded the calendar file.',
  },
});

/** One row of the ticket. */
export interface BookedTicketRow {
  /** When, Requested, Length, Format, Where, Paid, Due, Price or Payment. */
  readonly label: string;
  /** The row's words. */
  readonly value: string;
  /** The smaller line under them, or null. */
  readonly sub: string | null;
  /** The amount on the right, or null. */
  readonly amount: string | null;
  /** On Where for a place: the directions link; null otherwise. */
  readonly directionsHref: string | null;
}

/** The line about the client's Telegram chat. */
export interface BookedTelegramLine {
  /** sent: it went (or is going) to the chat; refused: Telegram would not deliver it, so the client opens the chat. */
  readonly kind: 'sent' | 'refused';
  /** The words. */
  readonly text: string;
  /** Open chat's address on a refused line; null otherwise. */
  readonly chatHref: string | null;
}

/** What the booked page knows. */
export interface BookedState {
  /** loading until the booking is read; not-found when it is not this client's. */
  readonly status: 'loading' | 'ready' | 'not-found';
  /** The offering's name. */
  readonly name: string;
  /** Its photo, or null. */
  readonly photoUrl: string | null;
  /** website-photo-zoom: the same photo framed as the studio set it (crop point and zoom), or null. */
  readonly photo: SitePhoto | null;
  /** "with <name>", or null. */
  readonly withName: string | null;
  /** The ticket's rows. */
  readonly rows: readonly BookedTicketRow[];
  /** The studio's preparation note and its heading, or null. */
  readonly preparation: { readonly heading: string; readonly text: string } | null;
  /** Add to Calendar's Google Calendar and Outlook links, or null before the booking is read. */
  readonly calendar: { readonly google: string; readonly outlook: string } | null;
  /** The Telegram line, or null while unknown or when nothing was sent. */
  readonly telegram: BookedTelegramLine | null;
  /** Where View my booking goes. */
  readonly bookingHref: string;
  /** Why the last action did not go through (the calendar file could not be opened), or null. */
  readonly problem: string | null;
}

/** What a drawing can ask the booked page to do. */
export interface BookedActions {
  /** Download the booking as a calendar file. */
  readonly addToCalendar: () => void;
}

/** What the booked page tells its host. */
export interface BookedEvents {
  /** The client downloaded the calendar file. */
  readonly addedToCalendar: Record<string, never>;
}
